"use client";

import { useState, useEffect, useRef } from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/hooks/useAuth";
import { Edit3, UserPlusIcon } from "lucide-react";
import { logger } from "@/utils/logger";

// User creation schema
const userCreationSchema = z.object({
  username: z.string().email({
    message: "Username must be a valid email address.",
  }),
  first_name: z.string().min(1, {
    message: "First name is required.",
  }),
  last_name: z.string().min(1, {
    message: "Last name is required.",
  }),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters.",
  }),
  superuser: z.boolean(),
  disabled: z.boolean(),
});

type UserCreationValues = z.infer<typeof userCreationSchema>;
type User = {
  _id: string;
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  disabled: boolean;
  superuser: boolean;
  created_at?: string;
  updated_at?: string;
  roles: string[];
};

interface UserManagementProps {
  apiBaseUrl: string;
}

const UserManagement: React.FC<UserManagementProps> = ({ apiBaseUrl }) => {
  const { user: currentUser, token } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Ref for daisyUI modal
  const createUserModalRef = useRef<HTMLDialogElement>(null);

  // Initialize user creation form
  const userCreationForm = useForm<UserCreationValues>({
    resolver: zodResolver(userCreationSchema),
    defaultValues: {
      username: "",
      first_name: "",
      last_name: "",
      password: "",
      superuser: false,
      disabled: false,
    },
  });

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Added token dependency
  // Function to fetch all users
  const fetchUsers = async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      // Make sure we're using the correct endpoint path without trailing slash
      const response = await fetch(`${apiBaseUrl}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch users");
      }
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      logger.error("Error fetching users:", error);
      alert("Error: Failed to load users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Function to create a new user
  const createUser = async (values: UserCreationValues) => {
    if (!token || !currentUser?.superuser) return;

    setIsCreating(true);
    try {
      const response = await fetch(`${apiBaseUrl}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to create user");
      }

      // Show success message
      alert("User created successfully");

      // Refresh the user list
      fetchUsers();

      // Close the dialog and reset form
      createUserModalRef.current?.close(); // Close daisyUI modal
      userCreationForm.reset();
    } catch (error) {
      logger.error("Error creating user:", error);
      // Display error message
      alert(
        `Error: ${
          error instanceof Error ? error.message : "Failed to create user"
        }`
      );
    } finally {
      setIsCreating(false);
    }
  };

  // Function to toggle user disabled status
  // @ts-ignore
  const toggleUserStatus = async (userId: string, currentStatus: boolean) => {
    if (!token || !currentUser?.superuser) return;

    try {
      const response = await fetch(`${apiBaseUrl}/users/${userId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          disabled: !currentStatus,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update user status");
      }

      // Show success message
      alert(`User ${currentStatus ? "enabled" : "disabled"} successfully`);

      // Refresh the user list
      fetchUsers();
    } catch (error) {
      logger.error("Error updating user status:", error);
      // Display error message
      alert("Error: Failed to update user status");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">User Management</h1>
        <button
          className="btn btn-primary"
          onClick={() => createUserModalRef.current?.showModal()}
        >
          <UserPlusIcon className="mr-2 h-4 w-4" />
          Create User
        </button>
        <dialog id="create_user_modal" className="modal" ref={createUserModalRef}>
          <div className="modal-box">
            <h3 className="font-bold text-lg">Create New User</h3>
            <p className="py-4 text-sm">
              Create a new user account. Only superusers can create new accounts.
            </p>
            <form
              onSubmit={userCreationForm.handleSubmit(createUser)}
              className="space-y-4"
            >
              <label className="form-control w-full">
                <div className="label">
                  <span className="label-text">First Name</span>
                </div>
                <input
                  type="text"
                  placeholder="John"
                  className={`input input-bordered w-full ${
                    userCreationForm.formState.errors.first_name
                      ? "input-error"
                      : ""
                  }`}
                  {...userCreationForm.register("first_name")}
                />
                {userCreationForm.formState.errors.first_name && (
                  <div className="label">
                    <span className="label-text-alt text-error">
                      {userCreationForm.formState.errors.first_name.message}
                    </span>
                  </div>
                )}
              </label>
              <label className="form-control w-full">
                <div className="label">
                  <span className="label-text">Last Name</span>
                </div>
                <input
                  type="text"
                  placeholder="Doe"
                  className={`input input-bordered w-full ${
                    userCreationForm.formState.errors.last_name
                      ? "input-error"
                      : ""
                  }`}
                  {...userCreationForm.register("last_name")}
                />
                {userCreationForm.formState.errors.last_name && (
                  <div className="label">
                    <span className="label-text-alt text-error">
                      {userCreationForm.formState.errors.last_name.message}
                    </span>
                  </div>
                )}
              </label>
              <label className="form-control w-full">
                <div className="label">
                  <span className="label-text">Email</span>
                </div>
                <input
                  type="email"
                  placeholder="email@example.com"
                  className={`input input-bordered w-full ${
                    userCreationForm.formState.errors.username
                      ? "input-error"
                      : ""
                  }`}
                  {...userCreationForm.register("username")}
                />
                {userCreationForm.formState.errors.username && (
                  <div className="label">
                    <span className="label-text-alt text-error">
                      {userCreationForm.formState.errors.username.message}
                    </span>
                  </div>
                )}
              </label>
              <label className="form-control w-full">
                <div className="label">
                  <span className="label-text">Password</span>
                </div>
                <input
                  type="password"
                  placeholder="••••••••"
                  className={`input input-bordered w-full ${
                    userCreationForm.formState.errors.password
                      ? "input-error"
                      : ""
                  }`}
                  {...userCreationForm.register("password")}
                />
                {userCreationForm.formState.errors.password && (
                  <div className="label">
                    <span className="label-text-alt text-error">
                      {userCreationForm.formState.errors.password.message}
                    </span>
                  </div>
                )}
              </label>
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-4">
                  <Controller
                      name="superuser"
                      control={userCreationForm.control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary"
                          checked={field.value}
                          onChange={field.onChange}
                          ref={field.ref}
                          />
                      )}
                    />
                  <span className="label-text">Superuser</span>
                </label>
                 <p className="text-xs text-base-content/70 pl-10">Grant admin privileges to this user</p>
              </div>
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-4">
                   <Controller
                      name="disabled"
                      control={userCreationForm.control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                          ref={field.ref}
                        />
                      )}
                    />
                  <span className="label-text">Disabled</span>
                 </label>
                  <p className="text-xs text-base-content/70 pl-10">Prevent this user from logging in</p>
              </div>
              <div className="modal-action mt-6">
                 <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => createUserModalRef.current?.close()}
                  >
                    Cancel
                  </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <>
                      <span className="loading loading-spinner"></span>
                      Creating...
                    </>
                  ) : (
                    "Create User"
                  )}
                </button>
              </div>
            </form>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button>close</button>
          </form>
        </dialog>
      </div>

      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title">Users</h2>
          <p className="text-sm text-base-content/70">
            Manage user accounts in the system. Only superusers can modify user
            accounts.
          </p>
          <div className="divider"></div>

          {isLoading ? (
            <div className="flex justify-center items-center h-32">
               <span className="loading loading-spinner"></span>
            </div>
          ) : (
             <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Role</th>
                    {currentUser?.superuser && <th className="text-center">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user._id}>
                        <td>{`${user.first_name} ${user.last_name}`}</td>
                        <td className="font-medium">{user.username}</td>
                        <td>
                          <span
                            className={`badge select-none ${
                              user.disabled ? "badge-error" : "badge-success"
                            } font-mono badge-sm`}
                          >
                            {user.disabled ? "Disabled" : "Active"}
                          </span>
                        </td>
                        <td>
                          {user.roles.map(role => (
                            <span key={role} className="badge badge-info font-mono badge-sm not-first:ml-2 select-none">
                              {role.charAt(0).toUpperCase() + role.slice(1)}
                            </span>
                          ))}
                          {user.superuser && (
                            <span className="badge badge-neutral font-mono badge-sm not-first:ml-2 select-none">
                              Superuser
                            </span>
                          )}
                        </td>
                        {currentUser?.superuser && (
                        <td className="text-center">
                          <span className="tooltip tooltip-left tooltip-neutral cursor-pointer" data-tip="Edit User">
                            <Edit3 className="w-4 h-4 text-base-content/70 hover:text-base-content" />
                          </span>
                        </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
